import React from "react";
import { TabGroup, TabList, TabPanels, TabPanel, Tab } from "@headlessui/react";

export type TabsShadow = "none" | "xs" | "sm" | "custom";

export type TabItem = {
  key: string;
  label: React.ReactNode;
  icon?: React.ReactNode;
};

export interface TabsProps {
  items: TabItem[];
  /** Controlled selected key */
  value?: string;
  /** Uncontrolled initial key */
  defaultValue?: string;
  /** Called with the item's key */
  onChange?: (key: string) => void;
  /** Optional right-side action (e.g., button group) */
  rightAction?: React.ReactNode;
  /** Size for paddings/fonts */
  size?: "sm" | "md";
  /** Visual customization - background of the header container */
  headerBgClass?: string; // default bg-transparent
  /** Visual customization - border around header container */
  headerBorderClass?: string; // default border-b border-gray-200
  /** Visual customization - header container shadow */
  headerShadow?: TabsShadow;
  headerShadowClass?: string; // used when headerShadow === "custom"
  /** Active/Inactive styles for tabs */
  activeBgClass?: string; // default bg-blue-50
  activeTextClass?: string; // default text-blue-700
  activeBorderClass?: string; // default border-blue-500
  inactiveTextClass?: string; // default text-gray-500
  hoverBgClass?: string; // default hover:bg-gray-50
  hoverTextClass?: string; // default hover:text-gray-700
  /** Additional classes for the header wrapper */
  className?: string;
  /** Panels to render, same order as items */
  children?: React.ReactNode;
}

/**
 * Reusable Tabs component built on @headlessui/react v2.
 * Focuses on color, border, shadow customization to match design tokens.
 */
export default function Tabs({
  items,
  value,
  defaultValue,
  onChange,
  rightAction,
  size = "md",
  headerBgClass = "bg-transparent",
  headerBorderClass = "border-b border-gray-200",
  headerShadow = "none",
  headerShadowClass = "",
  activeBgClass: _activeBgClass = "bg-blue-50",
  activeTextClass = "text-blue-700",
  activeBorderClass = "border-blue-500",
  inactiveTextClass = "text-slate-800",
  hoverBgClass = "hover:bg-gray-50",
  hoverTextClass = "hover:text-slate-900",
  className = "",
  children,
}: TabsProps) {
  const selectedIndex =
    value !== undefined
      ? Math.max(
          0,
          items.findIndex((i) => i.key === value)
        )
      : undefined;

  const defaultIndex =
    defaultValue !== undefined
      ? Math.max(
          0,
          items.findIndex((i) => i.key === defaultValue)
        )
      : 0;

  const headerShadowCn =
    headerShadow === "none"
      ? ""
      : headerShadow === "xs"
        ? "shadow-xs"
        : headerShadow === "sm"
          ? "shadow-sm"
          : headerShadowClass;

  const pad = size === "sm" ? "px-3 py-2.5 text-xs" : "px-3 py-2.5 text-sm";
  const iconSize = size === "sm" ? "h-3.5 w-3.5" : "h-4 w-4";

  return (
    <TabGroup
      {...(selectedIndex !== undefined ? { selectedIndex } : { defaultIndex })}
      onChange={(index) => {
        const key = items[index]?.key;
        if (key && onChange) onChange(key);
      }}
    >
      <div
        className={`${headerBgClass} ${headerBorderClass} ${headerShadowCn} ${className}`.trim()}
      >
        <div className="-mb-px flex flex-col sm:flex-row sm:justify-between">
          <TabList className="flex flex-wrap gap-1 sm:gap-1 sm:space-x-1 w-full min-w-0">
            {items.map((item) => (
              <Tab
                key={item.key}
                className={({ selected }) =>
                  [
                    "group inline-flex items-center justify-center rounded-t-lg transition-colors duration-200 flex-shrink-0 sm:flex-none max-w-full border-b-2 focus:outline-none focus-visible:outline-none focus:ring-0 focus-visible:ring-0 ring-offset-0 focus:ring-offset-0",
                    pad,
                    selected
                      ? `${activeTextClass} ${activeBorderClass}`
                      : `${inactiveTextClass} ${hoverTextClass} ${hoverBgClass} border-transparent`,
                  ].join(" ")
                }
              >
                {({ selected }) => (
                  <>
                    {item.icon && (
                      <span
                        className={`mr-1.5 sm:mr-2 ${iconSize} flex-shrink-0 ${
                          selected ? activeTextClass : "text-gray-400 group-hover:text-gray-500"
                        }`}
                      >
                        {item.icon}
                      </span>
                    )}
                    <span className="break-words text-center">{item.label}</span>
                  </>
                )}
              </Tab>
            ))}
          </TabList>
          {rightAction && (
            <div className="flex items-center mt-3 sm:mt-0 sm:ml-4">{rightAction}</div>
          )}
        </div>
      </div>

      {children && (
        <TabPanels>
          {React.Children.map(children, (child, i) => (
            <TabPanel key={i}>{child}</TabPanel>
          ))}
        </TabPanels>
      )}
    </TabGroup>
  );
}
